import React, { useState, useEffect, useRef } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Cookies from 'js-cookie';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css';
import { FaYoutube, FaFilePdf } from 'react-icons/fa';
import { BASE_URL } from '../config/constants';
const ShowLessons = () => {
	const { id, lessonId } = useParams();
	const [lessonDetails, setLessonDetails] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [lessons, setLessons] = useState([]);
	const [question, setQuestion] = useState({});
	const [selectedAnswer, setSelectedAnswer] = useState(null);
	const [feedbackMessage, setFeedbackMessage] = useState('');
	const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
	const videoRef = useRef(null);

	const token = Cookies.get('token');
	const baseUrl = BASE_URL;
	useEffect(() => {
		const fetchLessonDetails = async () => {
			setIsLoading(true);
			try {
				const response = await fetch(`${baseUrl}/api/v1/lessons/${lessonId}`, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				});
				const data = await response.json();
				setLessonDetails(data);
				setQuestion(data.question);
			} catch (error) {
				console.error('Error fetching lesson details:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchLessonDetails();
	}, [lessonId, token]);

	useEffect(() => {
		const fetchLessons = async () => {
			try {
				const response = await fetch(`${baseUrl}/api/v1/courses/${id}`, {
					method: 'GET',
					headers: {
						Authorization: `Bearer ${token}`,
						'Content-Type': 'application/json',
					},
				});
				const data = await response.json();
				setLessons(data.lessons);
			} catch (error) {
				console.error('Error fetching lessons data:', error);
			}
		};

		fetchLessons();
	}, [id, token]);

	useEffect(() => {
		setSelectedAnswer(null);
		setFeedbackMessage('');
	}, [lessonId]);

	useEffect(() => {
		if (videoRef.current) {
			const player = new Plyr(videoRef.current, {
				controls: [
					'play-large',
					'restart',
					'rewind',
					'play',
					'fast-forward',
					'progress',
					'current-time',
					'duration',
					'mute',
					'volume',
					'captions',
					'settings',
					'pip',
					'airplay',
					'fullscreen',
				],
			});

			// Store the player instance to use later
			videoRef.current.player = player;

			// Function to add the overlay div
			const addOverlay = () => {
				const fullscreenElement = document.fullscreenElement;
				if (fullscreenElement) {
					const overlay = document.createElement('div');
					overlay.className = 'absolute top-0 left-0 w-full z-[1000]';
					overlay.style.height = 'calc(100% - 75px)';
					overlay.onclick = handlePlayPause;
					fullscreenElement.appendChild(overlay);
				}
			};

			// Function to remove the overlay div
			const removeOverlay = () => {
				const fullscreenElement = document.fullscreenElement;
				if (fullscreenElement) {
					const overlay = fullscreenElement.querySelector('.absolute');
					if (overlay) {
						fullscreenElement.removeChild(overlay);
					}
				}
			};

			// Add event listeners for fullscreen changes
			player.on('enterfullscreen', () => {
				setIsFullscreen(true);
				addOverlay();
			});

			player.on('exitfullscreen', () => {
				setIsFullscreen(false);
				removeOverlay();
			});

			return () => {
				player.destroy();
			};
		}
	}, [lessonDetails]);

	// Debugging: Log fullscreen state
	useEffect(() => {
		console.log('Fullscreen state:', isFullscreen);
	}, [isFullscreen]);

	if (isLoading) {
		return (
			<main className='h-[80vh] bg-main text-xl md:text-4xl text-white flex justify-center items-center'>
				Loading...
			</main>
		);
	}

	const videoId = lessonDetails?.video?.URL.split('/').pop();

	const handleQuestion = () => {
		if (selectedAnswer === null) {
			setFeedbackMessage('Please select an answer before submitting.');
			return;
		}

		if (question.correctAnswer === parseInt(selectedAnswer)) {
			setFeedbackMessage('Your answer is correct! ✅');
		} else {
			setFeedbackMessage('Your answer is incorrect. Try again! ❌');
		}
	};

	const handlePlayPause = () => {
		if (videoRef.current && videoRef.current.player) {
			const player = videoRef.current.player;

			// Check if the video is in fullscreen mode
			if (document.fullscreenElement) {
				// In fullscreen, toggle play/pause
				if (player.paused) {
					player.play();
				} else {
					player.pause();
				}
			} else {
				// If not in fullscreen, toggle play/pause
				if (player.paused) {
					player.play();
				} else {
					player.pause();
				}
			}
		}
	};

	const handleDownloadPdf = () => {
		if (lessonDetails.file && lessonDetails.file.path) {
			const pdfUrl = `${baseUrl}${lessonDetails.file.path}`;
			const link = document.createElement('a');
			link.href = pdfUrl;
			link.target = '_blank';
			link.download = 'lesson.pdf';
			link.click();
		} else {
			alert('No PDF file available for this lesson.');
		}
	};

	return (
		<main className='py-4'>
			<section className='container bg-section flex flex-col md:flex-row-reverse gap-4 p-6'>
				<div className='w-full md:w-4/6 justify-center rounded overflow-hidden'>
					<div className='video-container relative'>
						<div
							ref={videoRef}
							data-plyr-provider='youtube'
							data-plyr-embed-id={videoId}
							className='absolute top-0 left-0'></div>
						<div
							className='absolute top-0 left-0 w-full'
							style={{ height: 'calc(100% - 75px)' }}
							onClick={handlePlayPause}></div>
					</div>

					<div className='flex items-center gap-2 my-4 w-8'>
						<FaFilePdf
							className='text-red-600 cursor-pointer w-full h-full'
							onClick={handleDownloadPdf}
							title='Download PDF'
						/>
					</div>
					<button
						onClick={handleDownloadPdf}
						className='text-lg font-semibold'>
						Download lesson
					</button>

					{question && question.type === 'MCQ' && (
						<div className='mt-4 p-4 bg-gray-200 rounded shadow'>
							<h2 className='text-lg font-semibold mb-2'>
								{question.question}
							</h2>
							<ul className='list-none pl-5'>
								{question.options.map((option, index) => (
									<li
										key={index}
										className='mb-1'>
										<label>
											<input
												type='radio'
												name='question'
												value={index + 1}
												className='mr-2'
												onChange={(e) => setSelectedAnswer(e.target.value)}
											/>
											{option}
										</label>
									</li>
								))}
							</ul>

							<button
								onClick={handleQuestion}
								className='mt-4 bg-main text-white py-2 px-4 rounded hover:bg-secondary transition-all'>
								Submit Answer
							</button>

							{feedbackMessage && (
								<p
									className={`mt-4 text-lg ${
										feedbackMessage.includes('incorrect')
											? 'text-red-600'
											: 'text-green-600'
									}`}>
									{feedbackMessage}
								</p>
							)}
						</div>
					)}
				</div>

				<aside className='w-full md:w-2/6 flex flex-col gap-1'>
					{lessons.length > 0 ? (
						lessons.map((lesson) => (
							<NavLink
								to={`/my-learning/${id}/${lesson.id}`}
								key={lesson.id}
								className={({ isActive }) =>
									isActive
										? 'customLesson p-2 bg-secondary text-white rounded flex gap-2 items-center'
										: 'customLesson p-2 bg-main text-white rounded flex gap-2 items-center'
								}>
								<div className='text-lg'>
									<FaYoutube />
								</div>
								<h2>{lesson.title}</h2>
							</NavLink>
						))
					) : (
						<p>No lessons available</p>
					)}
					<NavLink
						to={`/my-learning/${id}/final-test`}
						className=' p-2 bg-main text-white rounded flex gap-2 items-center'>
						Final Test
					</NavLink>
				</aside>
			</section>
		</main>
	);
};

export default ShowLessons;
